import { useMemo } from "react";
import { useRequest } from "@/request";

export const NotificationSeverity = {
  Info: 0,
  Warning: 1,
  Critical: 2,
} as const;
export type NotificationSeverity = (typeof NotificationSeverity)[keyof typeof NotificationSeverity];

export const NotificationActionKind = {
  None: "none",
  TicketDetail: "ticket_detail",
  SystemOperation: "system_operation",
} as const;
export type NotificationActionKind = (typeof NotificationActionKind)[keyof typeof NotificationActionKind];

export type NotificationDto = {
  cursor: number;
  notificationId: string;
  type: string;
  severity: NotificationSeverity;
  aggregateType: string;
  aggregateId: string;
  aggregateVersion: number;
  title: string;
  body: string;
  actionKind: string;
  actionTargetId: string | null;
  actorAgentId: string | null;
  creationTime: string;
  readTime: string | null;
  isTargetAccessible: boolean;
};

export type NotificationListResultDto = {
  items: NotificationDto[];
  nextCursor: number | null;
  hasMore: boolean;
};

export type NotificationChangesResultDto = {
  items: NotificationDto[];
  nextCursor: number;
  hasMore: boolean;
};

export type NotificationUnreadCountDto = {
  count: number;
};

export type MarkAllNotificationsReadResultDto = {
  throughCursor: number;
  affectedCount: number;
  unreadCount: number;
};

export type WebPushConfigDto = {
  enabled: boolean;
  applicationServerKey: string | null;
};

export type WebPushSubscriptionInput = {
  endpoint: string;
  expirationTime: string | null;
  keys: {
    p256dh: string;
    auth: string;
  };
};

export type WebPushSubscriptionResultDto = {
  renewalToken: string;
};

export type GetNotificationListInput = {
  beforeCursor?: number;
  limit?: number;
  unreadOnly?: boolean;
};

export type GetNotificationChangesInput = {
  afterCursor: number;
  limit?: number;
};

function buildQuery(values: Record<string, boolean | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function useNotificationApi() {
  const { request } = useRequest();

  return useMemo(
    () => ({
      getList: (input: GetNotificationListInput = {}, signal?: AbortSignal) =>
        request<NotificationListResultDto>(
          `notifications${buildQuery({ BeforeCursor: input.beforeCursor, Limit: input.limit, UnreadOnly: input.unreadOnly })}`,
          { method: "GET", signal, skipErrorToast: true },
        ),
      getChanges: (input: GetNotificationChangesInput) =>
        request<NotificationChangesResultDto>(`notifications/changes${buildQuery({ AfterCursor: input.afterCursor, Limit: input.limit })}`, {
          method: "GET",
          skipErrorToast: true,
        }),
      getUnreadCount: (signal?: AbortSignal) =>
        request<NotificationUnreadCountDto>("notifications/unread-count", { method: "GET", signal, skipErrorToast: true }),
      markRead: (cursor: number) => request<void>(`notifications/${cursor}/read`, { method: "POST" }),
      markAllRead: (throughCursor?: number) =>
        request<MarkAllNotificationsReadResultDto>("notifications/read-all", {
          method: "POST",
          data: { throughCursor: throughCursor ?? null },
        }),
      getWebPushConfig: () =>
        request<WebPushConfigDto>("notifications/web-push/config", {
          method: "GET",
          skipErrorToast: true,
        }),
      upsertWebPushSubscription: (subscription: WebPushSubscriptionInput) =>
        request<WebPushSubscriptionResultDto>("notifications/web-push/subscriptions", {
          method: "PUT",
          data: subscription,
          skipErrorToast: true,
        }),
      deleteWebPushSubscription: (endpoint: string, renewalToken: string | null = null) =>
        request<void>("notifications/web-push/subscriptions", {
          method: "DELETE",
          data: { endpoint, renewalToken },
          skipErrorToast: true,
        }),
    }),
    [request],
  );
}
